import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Row, Table, Badge, Spinner, Container, Button } from 'react-bootstrap';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

function SalesMDView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Draft': return 'secondary';
      case 'Open': return 'primary';
      case 'Completed': return 'success';
      case 'Cancelled': return 'danger';
      default: return 'warning';
    }
  };

  const getAddressString = (address) => {
    if (!address) return '-';
    return [
      address.customeraddress,
      address.city?.cityname,
      address.state?.statename,
      address.pincode,
      address.country?.countryname
    ].filter(Boolean).join(', ');
  };

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}sales/findById/${id}`, {
          headers: authHeader(),
        });
        setSalesData(response.data);
      } catch (error) {
        console.error("Error fetching sales data:", error);
        Swal.fire("Error", "Failed to load sales data", "error");
        navigate("/salesorderindex/1");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!salesData) {
    return <div className="alert alert-danger text-center mt-4">Sales Invoice not found</div>;
  }

  const grandTotal = Math.round(salesData.grandtotalproduct || 0);

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-primary mb-0">SALES INVOICE - VIEW</h5>
            <div>
              <Button variant="outline-primary" size="sm" className="me-2"
                onClick={() => navigate(`/Sales-Invoice-Manufacturing-Domestic/print/${id}`)}
              >
                <FaPrint className="me-1" />
                Print
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-1" />
                Back
              </Button>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Invoice No</small>
                <div className="fw-bold">{salesData.invoiceno || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Invoice Date</small>
                <div className="fw-bold">{formatDate(salesData.invoicedate)}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">DC No</small>
                <div className="fw-bold">{salesData.dcno || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg={getStatusBadge(salesData.status)} className="px-2 py-1">
                    {salesData.status || '-'}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Invoice Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Invoice Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Tally Reference No</small>
              <div className="fw-bold">{salesData.tallyreferenceno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">DC Date</small>
              <div className="fw-bold">{formatDate(salesData.dcdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Buyer Order No</small>
              <div className="fw-bold">{salesData.buyerorderno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Buyer Order Date</small>
              <div className="fw-bold">{formatDate(salesData.buyerorderdate)}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Policy No</small>
              <div className="fw-bold">{salesData.policyno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Valid Upto</small>
              <div className="fw-bold">{formatDate(salesData.validupto)}</div>
            </Col>
            <Col md={12}>
              <small className="text-muted">Reference</small>
              <div className="fw-bold">{salesData.reference || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Customer Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Customer Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Company Name</small>
              <div className="fw-bold">{salesData.customer?.companyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">GST No</small>
              <div className="fw-bold">{salesData.customer?.gstno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{salesData.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Phone</small>
              <div className="fw-bold">{salesData.contactno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{salesData.email || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Payment Term</small>
              <div className="fw-bold">{salesData.customer?.paymentterm || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Bill To and Ship To */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Bill To</h6>
            </div>
            <div className="p-3">
              {salesData.billto ? (
                <>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold mb-2">{getAddressString(salesData.billto)}</div>
                  <small className="text-muted">GST</small>
                  <div className="fw-bold">{salesData.billto.gstno || '-'}</div>
                </>
              ) : (
                <div className="text-muted">No billing address specified</div>
              )}
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Ship To</h6>
            </div>
            <div className="p-3">
              {salesData.shipto ? (
                <>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold mb-2">{getAddressString(salesData.shipto)}</div>
                  <small className="text-muted">Contact</small>
                  <div className="fw-bold mb-2">{salesData.shipto.contactperson || '-'}</div>
                  <small className="text-muted">Phone</small>
                  <div className="fw-bold">{salesData.shipto.contactno || '-'}</div>
                </>
              ) : (
                <div className="text-muted">No shipping address specified</div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Dispatch Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Dispatch Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Dispatch Through</small>
              <div className="fw-bold">{salesData.dispatchthrough || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Destination</small>
              <div className="fw-bold">{salesData.destination || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Terms of Delivery</small>
              <div className="fw-bold">{salesData.termsofdelivery || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Branch & Warehouse Information */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Branch Information</h6>
            </div>
            <div className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <small className="text-muted">Branch Name</small>
                  <div className="fw-bold">{salesData.branch?.branchname || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Branch GST</small>
                  <div className="fw-bold">{salesData.branch?.gstno || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Contact Person</small>
                  <div className="fw-bold">{salesData.branch?.contactperson || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Phone</small>
                  <div className="fw-bold">{salesData.branch?.phoneno || '-'}</div>
                </Col>
                <Col md={12}>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold">
                    {[
                      salesData.branch?.address,
                      salesData.branch?.city?.cityname,
                      salesData.branch?.state?.statename,
                      salesData.branch?.zipcode,
                      salesData.branch?.country?.countryname
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="shadow-sm border rounded bg-white h-100">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold">Warehouse Information</h6>
            </div>
            <div className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <small className="text-muted">Warehouse Name</small>
                  <div className="fw-bold">{salesData.warehouse?.warehousename || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Warehouse Type</small>
                  <div className="fw-bold">{salesData.warehouse?.warehousetype || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Contact Person</small>
                  <div className="fw-bold">{salesData.warehouse?.contactperson || '-'}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Phone</small>
                  <div className="fw-bold">{salesData.warehouse?.mobileno || '-'}</div>
                </Col>
                <Col md={12}>
                  <small className="text-muted">Address</small>
                  <div className="fw-bold">
                    {[
                      salesData.warehouse?.address,
                      salesData.warehouse?.city?.cityname,
                      salesData.warehouse?.state?.statename,
                      salesData.warehouse?.zipcode,
                      salesData.warehouse?.country?.countryname
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>

      {/* Products Table */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Products</h6>
        </div>
        <div className="p-0">
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center fw-bold py-2">#</th>
                  <th className="fw-bold py-2">Description</th>
                  <th className="fw-bold py-2">Part No</th>
                  <th className="fw-bold py-2">HSN Code</th>
                  <th className="fw-bold py-2">Serial No</th>
                  <th className="fw-bold py-2">MOC</th>
                  <th className="fw-bold py-2">Type</th>
                  <th className="fw-bold py-2">Size</th>
                  <th className="fw-bold py-2 text-center">Qty</th>
                  <th className="fw-bold py-2 text-center">UOM</th>
                  <th className="fw-bold py-2 text-end">Unit Price</th>
                  <th className="fw-bold py-2 text-end">Amount</th>
                  <th className="fw-bold py-2 text-center">GST %</th>
                  <th className="fw-bold py-2 text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {salesData.salesItems?.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      <div className="fw-semibold">{item.product?.description || '-'}</div>
                      {item.productremarks && item.productremarks !== '-' && (
                        <div className="text-muted small mt-1">
                          <small><strong>Remarks:</strong> {item.productremarks}</small>
                        </div>
                      )}
                    </td>
                    <td>{item.partno || '-'}</td>
                    <td>{item.hsnsacno || '-'}</td>
                    <td>{item.serialnumber || '-'}</td>
                    <td>{item.product?.moc || item.moc || '-'}</td>
                    <td>{item.product?.type || item.type || '-'}</td>
                    <td>{item.product?.size || item.size || '-'}</td>
                    <td className="text-center">{item.quantity || '0'}</td>
                    <td className="text-center">{item.uom || '-'}</td>
                    <td className="text-end">{formatCurrency(item.unitrateinr)}</td>
                    <td className="text-end">{formatCurrency(item.amount)}</td>
                    <td className="text-center">{item.gst || 0}%</td>
                    <td className="text-end fw-bold">{formatCurrency(item.totalinr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="13" className="text-end fw-bold py-2">Products Total:</td>
                  <td className="text-end fw-bold py-2">{formatCurrency(salesData.nettotalproduct)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>

      {/* Additional Charges */}
      {salesData.additionalCharges?.length > 0 && (
        <div className="shadow-sm border rounded bg-white mb-3">
          <div className="bg-light py-2 px-3 border-bottom">
            <h6 className="mb-0 fw-bold">Additional Charges</h6>
          </div>
          <div className="p-0">
            <div className="table-responsive">
              <Table bordered size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-center fw-bold py-2">#</th>
                    <th className="fw-bold py-2">Charge Type</th>
                    <th className="fw-bold py-2 text-end">Amount</th>
                    <th className="fw-bold py-2 text-center">GST %</th>
                    <th className="fw-bold py-2 text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {salesData.additionalCharges.map((charge, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{charge.chargetype || '-'}</td>
                      <td className="text-end">{formatCurrency(charge.chargeamount)}</td>
                      <td className="text-center">{charge.chargegst || 0}%</td>
                      <td className="text-end fw-bold">{formatCurrency(charge.chargetotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="4" className="text-end fw-bold py-2">Charges Total:</td>
                    <td className="text-end fw-bold py-2">{formatCurrency(salesData.additionalchargestotal)}</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Calculation Summary */}
      <Row className="mb-3">
        <Col lg={6} className="ms-auto">
          <div className="shadow-sm border rounded bg-white">
            <div className="bg-light py-2 px-3 border-bottom">
              <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
            </div>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Products Total:</span>
                <span className="fw-bold">{formatCurrency(salesData.nettotalproduct)}</span>
              </div>
              {salesData.additionalCharges?.length > 0 && (
                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                  <span className="fw-semibold">Additional Charges:</span>
                  <span className="fw-bold">{formatCurrency(salesData.additionalchargestotal)}</span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="fw-semibold">Gross Total:</span>
                <span className="fw-bold">{formatCurrency(salesData.grosstotal)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-top">
                <span className="fw-bold">Grand Total (Rounded)</span>
                <span className="fw-bold text-primary">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-1" />
          Back to List
        </Button>
      </div>
    </Container>
  );
}

export default SalesMDView;